export const ANALYZE_END_OF_CALL_REPORT_KEY = 'analyze_end_of_call_report' as const;
export const INCOMING_CALL_TRIAGE_KEY = 'incoming_call_triage' as const;

export const CALL_PURPOSE_KEYS = [
  'new_project_opportunity',
  'existing_project_service_issue',
  'warranty_callback',
  'billing',
  'scheduling',
  'vendor',
  'employment',
  'solicitor',
  'spam_robocall',
  'wrong_number',
  'other',
] as const;

export const CALLER_RELATIONSHIP_KEYS = ['new_contact', 'existing_contact', 'unknown'] as const;
export const LEAD_ELIGIBILITY_KEYS = ['eligible', 'not_eligible', 'needs_review'] as const;
export const LOW_VALUE_CALL_PURPOSE_KEYS = ['solicitor', 'spam_robocall', 'wrong_number'] as const;

export type CallPurposeKey = (typeof CALL_PURPOSE_KEYS)[number];
export type CallerRelationshipKey = (typeof CALLER_RELATIONSHIP_KEYS)[number];
export type LeadEligibilityKey = (typeof LEAD_ELIGIBILITY_KEYS)[number];

export interface TriageOptionSetting<T extends string> {
  key: T;
  label: string;
  enabled: boolean;
  description: string;
  examples: string[];
}

export interface IncomingCallTriageSettings {
  purposes: Array<TriageOptionSetting<CallPurposeKey>>;
  callerRelationships: Array<TriageOptionSetting<CallerRelationshipKey>>;
  leadEligibilityOptions: Array<TriageOptionSetting<LeadEligibilityKey>>;
  lowValuePurposes: CallPurposeKey[];
  leadLoopEligibleValue: 'eligible';
  fallbackPurpose: CallPurposeKey;
  fallbackCallerRelationship: CallerRelationshipKey;
  fallbackLeadEligibility: LeadEligibilityKey;
}

export interface AnalyzeEndOfCallSettings {
  capabilities: string[];
}

export interface CallTriageSettingsStore {
  select<T = Record<string, unknown>>(
    table: string,
    query?: Record<string, string | undefined>,
  ): Promise<T[]>;
}

/** Load the portal-owned Switch settings, failing safely to the included defaults. */
export async function loadIncomingCallTriageSettings(
  store: CallTriageSettingsStore,
  clientId: string,
): Promise<IncomingCallTriageSettings> {
  try {
    const definitions = await store.select<{ id: string; default_settings: unknown }>('switch_definitions', {
      select: 'id,default_settings',
      key: `eq.${INCOMING_CALL_TRIAGE_KEY}`,
      limit: '1',
    });
    const definition = definitions[0];
    if (!definition) return normalizeIncomingCallTriageSettings(DEFAULT_INCOMING_CALL_TRIAGE_SETTINGS);
    const portals = await store.select<{ settings: unknown }>('portal_switches', {
      select: 'settings',
      client_id: `eq.${clientId}`,
      switch_definition_id: `eq.${definition.id}`,
      enabled: 'eq.true',
      operational_status: 'eq.active',
      limit: '1',
    });
    return normalizeIncomingCallTriageSettings(portals[0]?.settings ?? definition.default_settings);
  } catch {
    // Ingestion and reads stay available during a control-plane outage. The
    // included defaults are fail-closed for LeadLoop (Needs Review).
    return normalizeIncomingCallTriageSettings(DEFAULT_INCOMING_CALL_TRIAGE_SETTINGS);
  }
}

export const DEFAULT_INCOMING_CALL_TRIAGE_SETTINGS: IncomingCallTriageSettings = {
  purposes: [
    option('new_project_opportunity', 'New Project / Opportunity', 'A potential paid project, additional service, new location, estimate, or separate scope of work.', ['New barn project', 'Estimate for additional work', 'New installation']),
    option('existing_project_service_issue', 'Existing Project / Service Issue', 'A question or problem tied to an existing active project or service relationship.', ['Project status', 'Problem with work completed yesterday', 'Existing service issue']),
    option('warranty_callback', 'Warranty / Callback', 'A warranty request, promised callback, or return visit tied to prior work.', ['Warranty claim', 'Requested callback', 'Return visit']),
    option('billing', 'Billing', 'A payment, invoice, receipt, or account-balance question.', ['Invoice question', 'Payment status', 'Receipt request']),
    option('scheduling', 'Scheduling', 'A calendar or arrival-time question that is not itself a new opportunity.', ['Confirm appointment time', 'Reschedule visit', 'Technician arrival window']),
    option('vendor', 'Vendor', 'A supplier, subcontractor, partner, or other business vendor.', ['Material delivery', 'Supplier account', 'Subcontractor coordination']),
    option('employment', 'Employment', 'A job seeker, applicant, or employment-related inquiry.', ['Job application', 'Hiring status', 'Employment verification']),
    option('solicitor', 'Solicitor', 'An unsolicited sales or promotional call to the business.', ['Marketing pitch', 'Unrequested service offer', 'Cold sales call']),
    option('spam_robocall', 'Spam / Robocall', 'Automated, fraudulent, abusive, or clearly irrelevant traffic.', ['Robocall', 'Scam message', 'Repeated silent spam']),
    option('wrong_number', 'Wrong Number', 'The caller intended to reach another person or business.', ['Mis-dialed number', 'Looking for another company']),
    option('other', 'Other', 'A purpose that does not confidently fit another enabled option.', ['Unclear purpose', 'General non-opportunity inquiry']),
  ],
  callerRelationships: [
    option('new_contact', 'New Contact', 'The caller is not yet matched to an existing Contact.', ['First-time caller', 'New prospect']),
    option('existing_contact', 'Existing Contact', 'The caller matches a Contact already known to the portal.', ['Current customer', 'Returning prospect', 'Known vendor contact']),
    option('unknown', 'Unknown', 'The relationship cannot be established confidently.', ['Anonymous caller', 'Insufficient identity']),
  ],
  leadEligibilityOptions: [
    option('eligible', 'Eligible', 'The call represents a potential paid project or opportunity and may enter LeadLoop.', ['New project', 'Additional paid scope', 'Estimate request']),
    option('not_eligible', 'Not Eligible', 'The call does not represent a new paid opportunity.', ['Billing question', 'Existing-project issue', 'Vendor call']),
    option('needs_review', 'Needs Review', 'The available analysis is insufficient to make a safe eligibility decision.', ['Ambiguous request', 'Incomplete report']),
  ],
  lowValuePurposes: ['solicitor', 'spam_robocall', 'wrong_number'],
  leadLoopEligibleValue: 'eligible',
  fallbackPurpose: 'other',
  fallbackCallerRelationship: 'unknown',
  fallbackLeadEligibility: 'needs_review',
};

/**
 * Whether a call should arrive with its Lead toggle already on.
 *
 * The reviewer's job is to correct exceptions, not to answer the same question
 * on every robocall. Three kinds of call answer themselves and start OFF:
 * a low-value purpose (solicitor, spam/robocall, wrong number), a call with no
 * transcript at all, and a call too short to have contained a request — dead
 * air, a hangup, a voicemail beep.
 *
 * Everything else starts ON. That direction is deliberate: a missed real lead
 * costs the business a job, while a wrongly-on call costs one click. This is a
 * DEFAULT, never a lock — an explicit stored decision always wins (see
 * `resolveLeadEligibility`).
 */
export const MIN_MEANINGFUL_CALL_SECONDS = 10;

export function autoLeadEligibility(input: {
  purpose?: string | null;
  transcript?: string | null;
  durationSeconds?: number | null;
  lowValuePurposes?: readonly string[];
}): LeadEligibilityKey {
  const lowValue = input.lowValuePurposes ?? LOW_VALUE_CALL_PURPOSE_KEYS;
  const purpose = callPurposeKey(input.purpose);
  if (purpose && lowValue.includes(purpose)) return 'not_eligible';

  if (!input.transcript?.trim()) return 'not_eligible';

  const seconds = input.durationSeconds;
  if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds < MIN_MEANINGFUL_CALL_SECONDS) {
    return 'not_eligible';
  }

  return 'eligible';
}

/**
 * The value the toggle should show.
 *
 * A stored `eligible` or `not_eligible` is a decision someone (or the analysis)
 * committed to, and it is returned untouched. `needs_review` and an absent
 * value are both "nobody has decided", so the automatic rule answers instead —
 * which is what stops a reviewer from facing a queue of undecided toggles.
 */
export function resolveLeadEligibility(
  stored: string | null | undefined,
  auto: Parameters<typeof autoLeadEligibility>[0],
): LeadEligibilityKey {
  const explicit = leadEligibilityKey(stored);
  if (explicit && explicit !== 'needs_review') return explicit;
  return autoLeadEligibility(auto);
}

export function normalizeIncomingCallTriageSettings(value: unknown): IncomingCallTriageSettings {
  const input = isRecord(value) ? value : {};
  const purposes = normalizeOptions(input.purposes ?? input.categories, DEFAULT_INCOMING_CALL_TRIAGE_SETTINGS.purposes, CALL_PURPOSE_KEYS);
  const callerRelationships = normalizeOptions(input.callerRelationships, DEFAULT_INCOMING_CALL_TRIAGE_SETTINGS.callerRelationships, CALLER_RELATIONSHIP_KEYS);
  const leadEligibilityOptions = normalizeOptions(input.leadEligibilityOptions, DEFAULT_INCOMING_CALL_TRIAGE_SETTINGS.leadEligibilityOptions, LEAD_ELIGIBILITY_KEYS);
  const enabledPurposes = new Set(purposes.filter((item) => item.enabled).map((item) => item.key));
  const allowedLowValuePurposes = new Set<CallPurposeKey>(LOW_VALUE_CALL_PURPOSE_KEYS);
  const lowValuePurposes = stringArray(input.lowValuePurposes ?? input.lowValueCategories, 20, 80)
    .map(callPurposeKey)
    .filter((item): item is CallPurposeKey => Boolean(
      item && enabledPurposes.has(item) && allowedLowValuePurposes.has(item),
    ));

  return {
    purposes,
    callerRelationships,
    leadEligibilityOptions,
    lowValuePurposes: [...new Set(lowValuePurposes)],
    leadLoopEligibleValue: 'eligible',
    fallbackPurpose: enabledValue(input.fallbackPurpose ?? input.fallbackCategory, purposes, 'other', callPurposeKey),
    fallbackCallerRelationship: enabledValue(input.fallbackCallerRelationship, callerRelationships, 'unknown', callerRelationshipKey),
    fallbackLeadEligibility: enabledValue(input.fallbackLeadEligibility, leadEligibilityOptions, 'needs_review', leadEligibilityKey),
  };
}

export function enabledCallPurposes(settings: IncomingCallTriageSettings): CallPurposeKey[] {
  return settings.purposes.filter((item) => item.enabled).map((item) => item.key);
}

export function resolveConfiguredCallPurpose(
  purpose: string | null | undefined,
  settings: IncomingCallTriageSettings,
): CallPurposeKey {
  const candidate = callPurposeKey(purpose);
  return candidate && enabledCallPurposes(settings).includes(candidate)
    ? candidate
    : settings.fallbackPurpose;
}

export function resolveConfiguredCallerRelationship(
  relationship: string | null | undefined,
  settings: IncomingCallTriageSettings,
): CallerRelationshipKey {
  const candidate = callerRelationshipKey(relationship);
  return candidate && settings.callerRelationships.some((item) => item.enabled && item.key === candidate)
    ? candidate
    : settings.fallbackCallerRelationship;
}

export function resolveConfiguredLeadEligibility(
  eligibility: string | null | undefined,
  settings: IncomingCallTriageSettings,
): LeadEligibilityKey {
  const candidate = leadEligibilityKey(eligibility);
  return candidate && settings.leadEligibilityOptions.some((item) => item.enabled && item.key === candidate)
    ? candidate
    : settings.fallbackLeadEligibility;
}

export function isLowValueCallPurpose(
  purpose: string | null | undefined,
  settings: IncomingCallTriageSettings,
): boolean {
  const candidate = callPurposeKey(purpose);
  return Boolean(candidate && settings.lowValuePurposes.includes(candidate));
}

export function isLeadLoopEligibleCall(
  eligibility: string | null | undefined,
  settings: IncomingCallTriageSettings,
): boolean {
  return leadEligibilityKey(eligibility) === settings.leadLoopEligibleValue;
}

export function validateIncomingCallTriageSettings(settings: IncomingCallTriageSettings): string[] {
  const issues: string[] = [];
  if (!settings.purposes.some((item) => item.enabled)) issues.push('Keep at least one call purpose available.');
  if (!settings.callerRelationships.some((item) => item.enabled)) issues.push('Keep at least one caller relationship available.');
  if (!settings.leadEligibilityOptions.some((item) => item.enabled && item.key === 'eligible')) issues.push('Eligible must remain available because it is the LeadLoop gate.');
  if (!settings.leadEligibilityOptions.some((item) => item.enabled && item.key === settings.fallbackLeadEligibility)) issues.push('The fallback Lead eligibility must be available.');
  const all = [...settings.purposes, ...settings.callerRelationships, ...settings.leadEligibilityOptions];
  if (all.some((item) => !item.label.trim() || !item.description.trim())) issues.push('Every triage option needs a label and description.');
  return issues;
}

export function callPurposeLabel(value: string | null | undefined): string {
  const key = callPurposeKey(value);
  return DEFAULT_INCOMING_CALL_TRIAGE_SETTINGS.purposes.find((item) => item.key === key)?.label ?? 'Other';
}

export function callerRelationshipLabel(value: string | null | undefined): string {
  const key = callerRelationshipKey(value);
  return DEFAULT_INCOMING_CALL_TRIAGE_SETTINGS.callerRelationships.find((item) => item.key === key)?.label ?? 'Unknown';
}

export function leadEligibilityLabel(value: string | null | undefined): string {
  const key = leadEligibilityKey(value);
  return DEFAULT_INCOMING_CALL_TRIAGE_SETTINGS.leadEligibilityOptions.find((item) => item.key === key)?.label ?? 'Needs Review';
}

function option<T extends string>(key: T, label: string, description: string, examples: string[]): TriageOptionSetting<T> {
  return { key, label, enabled: true, description, examples };
}

function normalizeOptions<T extends string>(
  value: unknown,
  defaults: Array<TriageOptionSetting<T>>,
  allowed: readonly T[],
): Array<TriageOptionSetting<T>> {
  const provided = Array.isArray(value) ? value.filter(isRecord) : [];
  const byKey = new Map(provided.map((item) => [typeof item.key === 'string' ? item.key : '', item]));
  return defaults.map((fallback) => {
    const item = byKey.get(fallback.key);
    return {
      key: fallback.key,
      label: stringValue(item?.label, fallback.label, 100),
      enabled: typeof item?.enabled === 'boolean' ? item.enabled : fallback.enabled,
      description: stringValue(item?.description, fallback.description, 600),
      examples: stringArray(item?.examples, 10, 180, fallback.examples),
    };
  }).filter((item) => allowed.includes(item.key));
}

function enabledValue<T extends string>(
  value: unknown,
  options: Array<TriageOptionSetting<T>>,
  fallback: T,
  parser: (value: unknown) => T | undefined,
): T {
  const parsed = parser(value);
  return parsed && options.some((item) => item.enabled && item.key === parsed) ? parsed : fallback;
}

function callPurposeKey(value: unknown): CallPurposeKey | undefined {
  return enumValue(value, CALL_PURPOSE_KEYS);
}

function callerRelationshipKey(value: unknown): CallerRelationshipKey | undefined {
  return enumValue(value, CALLER_RELATIONSHIP_KEYS);
}

export function leadEligibilityKey(value: unknown): LeadEligibilityKey | undefined {
  return enumValue(value, LEAD_ELIGIBILITY_KEYS);
}

function enumValue<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase() as T;
  return allowed.includes(normalized) ? normalized : undefined;
}

function stringValue(value: unknown, fallback: string, maxLength: number): string {
  return typeof value === 'string' && value.trim() ? value.trim().slice(0, maxLength) : fallback;
}

function stringArray(value: unknown, maxItems: number, maxLength: number, fallback: string[] = []): string[] {
  if (!Array.isArray(value)) return [...fallback];
  return value.filter((item): item is string => typeof item === 'string').map((item) => item.trim().slice(0, maxLength)).filter(Boolean).slice(0, maxItems);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
